import * as ClaimTypes from './claimTypes';

export interface IJWTIdentity {
  nbf: number;
  exp: number;
  iat: number;
  iss: string;
  id: number;
  sub: number;
  [key: string]: string | string[] | number,
}

const camelCaseReviver = (key: string, value: any) => {
  const result = value;
  if (result && typeof result === 'object') {
    Object.keys(value).forEach((k) => {
      if (/^[A-Z]/.test(k) && Object.hasOwnProperty.call(value, k)) {
        result[k.charAt(0).toLowerCase() + k.substring(1)] = value[k];
        delete result[k];
      }
    });
  }
  return result;
};

const b64DecodeUnicode = (str: string) => decodeURIComponent(
  atob(str)
    .split('')
    .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
    .join(''),
);

export const GetTimeUntilExpiry = (user: IJWTIdentity): number => {
  const unixTime = Math.floor(Date.now() / 1000);

  return user.exp - unixTime;
};

export const WasJustIssued = (user: IJWTIdentity): boolean => {
  // Allow 10 second difference
  const acceptableDiff = 10;
  const unixTime = Math.floor(Date.now() / 1000);

  return Math.abs(user.iat - unixTime) <= acceptableDiff;
};
export const IsClockSkewed = (user: IJWTIdentity): boolean => {
  // Allow 1 minute difference
  const acceptableDiff = 60;
  const unixTime = Math.floor(Date.now() / 1000);
  return Math.abs(user.iat - unixTime) >= acceptableDiff;
};

export const Parse = (token: string) => {
  const payloadBase64 = token.split('.')[1];
  const payloadDecoded = b64DecodeUnicode(payloadBase64);
  return JSON.parse(
    payloadDecoded,
    camelCaseReviver,
  ) as IJWTIdentity;
};

type ClaimValues = string | number | string[];
/**
 * Check if a user has a specific claim
 * @param user The user.
 * @param claimName The name of the claim the should have.
 * @param claimValue The value the claim should have.
 * @returns True if the user has the specific claim. Otherwise false.
 */
// eslint-disable-next-line max-len
const DoesUserHaveClaim = (user: IJWTIdentity, claimName: string, claimValue: string | number): boolean => {
  // Check that user has a claim with correct name
  if (Object.keys(user).includes(claimName)) {
    const userClaim = user[claimName];
    if (typeof userClaim === typeof claimValue) {
      return userClaim === claimValue;
    }
    const userClaimArray = userClaim as string[];
    return userClaimArray.findIndex((c) => c === claimValue) !== -1;
  }
  return false;
};

/**
 * Check if a user is in a specific role.
 * @param user The user.
 * @param roleName The name of the role.
 * @returns True if the user is in the role. Otherwise false.
 */
// eslint-disable-next-line max-len
export const IsUserInRole = (user: IJWTIdentity, roleName: string): boolean => DoesUserHaveClaim(user, ClaimTypes.Role, roleName);

type ClaimReturnType = ClaimValues | undefined;
// eslint-disable-next-line max-len
export const GetUserClaim = <T extends ClaimValues>(user: IJWTIdentity, claimName: string): ClaimReturnType => {
  const hasClaim = Object.keys(user).includes(claimName);
  if (!hasClaim) return undefined;

  const userClaim = user[claimName];
  return userClaim as T;
};
